import {Button} from '@dropbox/dig-components/buttons';
import {SegmentedControl} from '@dropbox/dig-components/controls';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {LockLine} from '@dropbox/dig-icons/assets';
import {DialLockMini, FolderMini} from '@dropbox/dig-illustrations';
import {useQueries} from '@tanstack/react-query';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {
  Employee,
  EmployeeFull,
  TeamService,
  TeamWithCountsNew,
  WorkstreamAccess,
  WorkstreamBasic,
  WorkstreamBasicWithOutcomes,
  WorkstreamEmployee,
  WorkstreamPrivacy,
} from 'client';
import {EmptyState} from 'components/DSYS/EmptyState';
import {GroupTabs} from 'components/DSYS/GroupTabs';
import {LabelGroup} from 'components/DSYS/LabelGroup';
import {Link} from 'components/DSYS/Link';
import {SkeletonFadeIn, useSkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {StatusButton} from 'components/DSYS/StatusButton';
import {ROUTE_PATHS} from 'constant';
import {getRoleLabel, getWorkstreamRoles, isValidRole} from 'helpers/workstream_roles';
import {t} from 'i18next';
import {useEffect, useMemo, useState} from 'react';
import {getService} from 'utilities';
import {logDashboardInteraction} from 'views/home/dashboard/dashboardAnalytics';
import {Tile} from 'views/home/dashboard/Tile';
import {TileHeaderLink} from 'views/home/dashboard/TileListFooter';
import {useWorkstream} from 'views/workstreams/hooks';
import {viaState} from 'views/workstreams/page/workstreamsAnalytics';
import {workstreamsPersonPath} from 'views/workstreams/page/workstreamsRoutes';
import {
  getStatusPriority,
  getWorkstreamDri,
  getWorkstreamUpdateVisualStatus,
  isWorkstreamStatusArchived,
} from 'views/workstreams/utils';
import {WorkstreamStatusUpdateModal} from 'views/workstreams/WorkstreamStatusUpdateModal';

import styles from './MyWork.module.css';
import {useCurrentHalfWorkstreamIds} from './useCurrentHalfWorkstreamIds';

const limitProjects = 9;
const MINE_MODE_ID = 'mine';
const TEAM_MODE_ID = 'team';
const CONTRIBUTOR_TAB_ID = 'contributor';
const ROLE_TAB_ORDER = [...getWorkstreamRoles().map((role) => role.id), CONTRIBUTOR_TAB_ID];

type WorkstreamWithRole = {
  role: string;
  workstream: WorkstreamBasicWithOutcomes;
};

const getRoleTabLabel = (roleId: string) => {
  if (roleId === CONTRIBUTOR_TAB_ID) {
    return t('workstream_contributor');
  }
  return getRoleLabel(roleId);
};

const isAssociationForEmployee = (
  association: WorkstreamEmployee,
  employee: Employee | EmployeeFull
) =>
  association.employee.user_id === employee.user_id || association.employee.ldap === employee.ldap;

const getEmployeeRoles = (
  workstream: WorkstreamBasicWithOutcomes,
  employee: Employee | EmployeeFull
) => {
  const roles = new Set<string>();

  for (const association of workstream.employee_associations) {
    if (!isAssociationForEmployee(association, employee)) {
      continue;
    }

    const role = association.dri;
    if (typeof role === 'string' && isValidRole(role)) {
      roles.add(role);
    }
  }

  return [...roles];
};

export const MyWork = ({
  employee,
  employeeTeams,
  source = 'profile',
  maxItems,
  embedded = false,
}: {
  employee: Employee | EmployeeFull;
  employeeTeams?: TeamWithCountsNew[];
  source?: string;
  maxItems?: number;
  /**
   * Render without the bordered card chrome or title (the host supplies them —
   * e.g. a dashboard Tile). Rows scroll to fill the available height instead of
   * paginating with a "show more" button.
   */
  embedded?: boolean;
}) => {
  const {workstream: tracker, isLoading: isTrackerLoading} = useWorkstream(employee.ldap);
  const [activeMode, setActiveMode] = useState(MINE_MODE_ID);
  const [activeTeamTab, setActiveTeamTab] = useState<string | null>(null);
  const [hasInitializedDefaultMode, setHasInitializedDefaultMode] = useState(false);

  const sortedEmployeeTeams = useMemo(
    () => sortEmployeeTeamsForTabs(employee, employeeTeams ?? []),
    [employee, employeeTeams]
  );
  const shouldShowTeamTabs = source === 'profile' && sortedEmployeeTeams.length > 0;
  // On profile, both sides show only the current half plus Enablement, like
  // the /workstreams page View all opens.
  const mineHalf = useCurrentHalfWorkstreamIds({
    ldap: employee.ldap,
    scope: 'person',
    enabled: source === 'profile',
  });
  const teamHalf = useCurrentHalfWorkstreamIds({
    ldap: employee.ldap,
    scope: 'team',
    enabled: shouldShowTeamTabs,
  });
  const isMineLoading = isTrackerLoading || mineHalf.isLoading;
  const mineVisibleWorkstreams = useMemo(
    () =>
      buildMineVisibleWorkstreams(employee, tracker).filter(
        ({workstream}) => !mineHalf.ids || mineHalf.ids.has(workstream.id)
      ),
    [employee, tracker, mineHalf.ids]
  );

  // `mode` (individual/team) rides along only where the toggle is shown;
  // `role` is the Individual side's role tab (DRI, Contributor, …).
  const profileActionLog = (
    action: string,
    {mode = activeMode, role}: {mode?: string; role?: string} = {}
  ) => {
    analyticsLogger().logEvent('PROFILE_INTERACTION', {
      action,
      source,
      mode: shouldShowTeamTabs ? getModeName(mode) : undefined,
      role,
    });
    if (source === 'dashboard') {
      logDashboardInteraction({
        action: action === 'role_tab' ? 'tab_click' : 'row_click',
        tileId: 'workstreams',
        target: action,
        value: role,
      });
    }
  };
  const shouldShowProfileScopeLink =
    source === 'profile' && Boolean(employee.ldap) && mineVisibleWorkstreams.length > 0;
  const profileScopePath = useMemo(() => workstreamsPersonPath(employee.ldap), [employee.ldap]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Existing issue; this suppression was only added to allow existing lint warnings to be upgraded to errors.
  const teamWorkstreamQueries = (useQueries({
    queries: shouldShowTeamTabs
      ? sortedEmployeeTeams.map((team) => ({
          queryKey: ['team', 'workstreams', team.slug],
          queryFn: () =>
            getService(TeamService).getTeamWorkstreamsApiV1TeamsSlugSlugWorkstreamsGet(
              team.slug ?? ''
            ),
          enabled: Boolean(team.slug),
        }))
      : [],
  }) ?? []) as Array<
    {data?: WorkstreamBasic[]; isLoading?: boolean; isPending?: boolean} | undefined
  >;
  const teamTabs = useMemo(
    () =>
      sortedEmployeeTeams.map((team, index) => {
        const query = teamWorkstreamQueries[index] as
          | {data?: WorkstreamBasic[]; isLoading?: boolean; isPending?: boolean}
          | undefined;
        return {
          id: getTeamTabId(team),
          team,
          isLoading:
            query == null ||
            (query.isLoading ?? false) ||
            (query.isPending ?? false) ||
            teamHalf.isLoading,
          workstreams: getVisibleTeamWorkstreams(
            (query?.data as WorkstreamBasic[] | undefined) ?? []
          ).filter((workstream) => !teamHalf.ids || teamHalf.ids.has(workstream.id)),
        };
      }),
    [sortedEmployeeTeams, teamWorkstreamQueries, teamHalf.ids, teamHalf.isLoading]
  );

  useEffect(() => {
    if (!shouldShowTeamTabs && activeMode !== MINE_MODE_ID) {
      setActiveMode(MINE_MODE_ID);
      return;
    }
  }, [activeMode, shouldShowTeamTabs]);

  useEffect(() => {
    const defaultTeamTabId = teamTabs[0]?.id ?? null;

    if (defaultTeamTabId === null) {
      setActiveTeamTab(null);
      return;
    }

    if (activeTeamTab === null || !teamTabs.some((teamTab) => teamTab.id === activeTeamTab)) {
      setActiveTeamTab(defaultTeamTabId);
    }
  }, [activeTeamTab, teamTabs]);

  useEffect(() => {
    setHasInitializedDefaultMode(false);
    setActiveMode(MINE_MODE_ID);
  }, [employee.ldap, shouldShowTeamTabs]);

  useEffect(() => {
    if (!shouldShowTeamTabs || hasInitializedDefaultMode) {
      return;
    }

    const hasLoadingTeamQuery = teamTabs.some((teamTab) => teamTab.isLoading);
    if (hasLoadingTeamQuery) {
      return;
    }

    const firstTeamWithWorkstreams = teamTabs.find((teamTab) => teamTab.workstreams.length > 0);
    if (mineVisibleWorkstreams.length === 0 && firstTeamWithWorkstreams) {
      setActiveMode(TEAM_MODE_ID);
      setActiveTeamTab(firstTeamWithWorkstreams.id);
    } else {
      setActiveMode(MINE_MODE_ID);
      setActiveTeamTab(firstTeamWithWorkstreams?.id ?? teamTabs[0]?.id ?? null);
    }

    setHasInitializedDefaultMode(true);
  }, [hasInitializedDefaultMode, mineVisibleWorkstreams.length, shouldShowTeamTabs, teamTabs]);

  // Grey out a side with nothing to show, but only once both sides have loaded
  // and the other side has something; when both are empty, both stay clickable
  // so each can show its empty state.
  const hasMineWorkstreams =
    mineVisibleWorkstreams.length > 0 ||
    (tracker?.dependencies ?? []).some(
      (dependency) =>
        dependency.dependent.name === 'Restricted' &&
        !dependency.dependent.archived &&
        !isWorkstreamStatusArchived(dependency.dependent)
    );
  const hasTeamWorkstreams = teamTabs.some((teamTab) => teamTab.workstreams.length > 0);
  const isModeDataLoaded = !isMineLoading && !teamTabs.some((teamTab) => teamTab.isLoading);
  const isMineDisabled = isModeDataLoaded && !hasMineWorkstreams && hasTeamWorkstreams;
  const isTeamDisabled = isModeDataLoaded && !hasTeamWorkstreams && hasMineWorkstreams;

  const segmentedControl = shouldShowTeamTabs ? (
    <SegmentedControl
      selectedValue={activeMode}
      onSelection={(value) => {
        if (!value || value === activeMode) return;
        setActiveMode(value as string);
        profileActionLog('mode_toggle', {mode: value as string});
      }}
    >
      <SegmentedControl.Option variant="text" value={MINE_MODE_ID} disabled={isMineDisabled}>
        {t('individual')}
      </SegmentedControl.Option>
      <SegmentedControl.Option variant="text" value={TEAM_MODE_ID} disabled={isTeamDisabled}>
        {t('team')}
      </SegmentedControl.Option>
    </SegmentedControl>
  ) : null;

  const body =
    shouldShowTeamTabs && activeMode === TEAM_MODE_ID ? (
      <>
        <div className={styles.groupTabsContainer}>
          <GroupTabs
            tabs={teamTabs.map((teamTab) => ({
              id: teamTab.id,
              label: teamTab.team.name ?? 'Team',
              count: 0,
              disabled: false,
            }))}
            activeTab={activeTeamTab}
            onTabClick={(tabId) => {
              if (tabId === activeTeamTab) return;
              setActiveTeamTab(tabId);
              profileActionLog('team_tab');
            }}
            totalCount={teamTabs.length}
            showAllTab={false}
            showCounts={false}
          />
        </div>
        {teamTabs
          .filter((teamTab) => teamTab.id === activeTeamTab)
          .map((teamTab) => (
            <TeamWorkstreamTab
              key={teamTab.team.team_id}
              employee={employee}
              team={teamTab.team}
              workstreams={teamTab.workstreams}
              isLoading={teamTab.isLoading}
              onClick={profileActionLog}
              source={source}
              maxItems={maxItems}
            />
          ))}
      </>
    ) : (
      <WorkstreamTab
        employee={employee}
        isLoading={isMineLoading}
        tracker={tracker}
        visibleWorkstreams={mineVisibleWorkstreams}
        onClick={profileActionLog}
        source={source}
        maxItems={maxItems}
        embedded={embedded}
      />
    );

  if (embedded) {
    return (
      <Stack gap="8" display="flex" height="100%">
        {segmentedControl ? (
          <Split>
            <Split.Item width="fill" />
            <Split.Item>{segmentedControl}</Split.Item>
          </Split>
        ) : null}
        {/* Real flex-item wrapper so the body gets a bounded height to scroll
            within. Stack's `style` lands on its inner element (whose parent is a
            plain wrapper div), so `flex: 1` only resolves on a Box like this. */}
        <Box display="flex" flexDirection="column" style={{flex: 1, minHeight: 0}}>
          {body}
        </Box>
      </Stack>
    );
  }

  // The same card as the home dashboard's Workstreams tile; only the list
  // paginates here instead of scrolling inside a fixed tile height.
  return (
    <Tile
      tileId="workstreams"
      labelTagName="h2"
      labelControl={segmentedControl}
      accessory={
        shouldShowProfileScopeLink ? (
          <TileHeaderLink
            to={profileScopePath}
            state={viaState('profile_card')}
            onClick={() => profileActionLog('view_all')}
          />
        ) : undefined
      }
    >
      {body}
    </Tile>
  );
};

const WorkstreamTab = ({
  employee,
  isLoading,
  tracker,
  visibleWorkstreams,
  onClick,
  source = 'profile',
  maxItems,
  embedded = false,
}: {
  employee: Employee | EmployeeFull;
  isLoading: boolean;
  tracker?: WorkstreamAccess;
  visibleWorkstreams: WorkstreamWithRole[];
  onClick: (action: string, props?: {role?: string}) => void;
  source?: string;
  maxItems?: number;
  embedded?: boolean;
}) => {
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [expandedTabs, setExpandedTabs] = useState<Record<string, boolean>>({});
  const [updateWorkstream, setUpdateWorkstream] = useState<WorkstreamBasic | undefined>(undefined);
  const fadeInRef = useSkeletonFadeIn(isLoading);

  const groupedWorkstreams = useMemo(
    () =>
      visibleWorkstreams.reduce<Record<string, number[]>>((acc, {role, workstream}) => {
        return {
          ...acc,
          [role]: [...(acc[role] ?? []), workstream.id],
        };
      }, {}),
    [visibleWorkstreams]
  );

  const tabs = useMemo(
    () =>
      ROLE_TAB_ORDER.map((roleId) => ({
        id: roleId,
        label: getRoleTabLabel(roleId),
        count: groupedWorkstreams[roleId]?.length ?? 0,
        disabled: false,
      })).filter((tab) => tab.count > 0),
    [groupedWorkstreams]
  );
  // A single role tab is just a label over the list, so only show tabs to switch between roles.
  const shouldShowTabs = tabs.length > 1;
  const defaultTabId = tabs[0]?.id ?? null;

  useEffect(() => {
    if (defaultTabId === null) {
      setActiveTab(null);
      return;
    }

    if (activeTab === null || !tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(defaultTabId);
    }
  }, [activeTab, defaultTabId, tabs]);

  const resolvedActiveTab = activeTab ?? defaultTabId;

  const visibleRows = useMemo(
    () =>
      resolvedActiveTab === null
        ? visibleWorkstreams
        : visibleWorkstreams.filter(({role}) => role === resolvedActiveTab),
    [resolvedActiveTab, visibleWorkstreams]
  );

  const expandedTabKey = resolvedActiveTab ?? 'default';
  const isExpanded = expandedTabs[expandedTabKey] ?? false;
  const limit = maxItems ?? limitProjects;
  // Embedded tiles own a bounded height and scroll, so they show every row
  // rather than paginating behind a "show more" button.
  const displayedRows = embedded
    ? visibleRows
    : visibleRows.slice(0, isExpanded ? undefined : limit);

  const restrictedWorkstreamCount =
    tracker?.dependencies.filter(
      (dependency) =>
        dependency.dependent.name === 'Restricted' &&
        !dependency.dependent.archived &&
        !isWorkstreamStatusArchived(dependency.dependent)
    ).length ?? 0;

  const tabsHeader = shouldShowTabs && (
    <div className={styles.groupTabsContainer}>
      <GroupTabs
        tabs={tabs}
        activeTab={resolvedActiveTab}
        onTabClick={(tabId) => {
          if (tabId === resolvedActiveTab) return;
          setActiveTab(tabId);
          onClick('role_tab', {role: tabId ?? undefined});
        }}
        totalCount={visibleWorkstreams.length}
        showAllTab={false}
      />
    </div>
  );

  const rows = displayedRows.map(({workstream}) => (
    <WorkstreamRow
      key={workstream.id}
      employee={employee}
      onClick={(action) => onClick(action, {role: resolvedActiveTab ?? undefined})}
      onUpdateWorkstream={setUpdateWorkstream}
      showContributorChip
      source={source}
      workstream={workstream}
    />
  ));

  const restrictedNote = restrictedWorkstreamCount > 0 && (
    <Box paddingTop="8">
      <Text size="small" color="subtle">
        {t('restricted_workstreams_count', {count: restrictedWorkstreamCount})}
      </Text>
    </Box>
  );

  const statusUpdateModal = (
    <WorkstreamStatusUpdateModal
      workstreamId={updateWorkstream?.id}
      onClose={() => setUpdateWorkstream(undefined)}
      source={source}
    />
  );

  if (embedded) {
    if (isLoading) return <ProjectSkeleton />;
    return (
      // The flex item the tile body bounds, so the list still scrolls within it.
      <Box ref={fadeInRef} display="flex" flexDirection="column" style={{flex: 1, minHeight: 0}}>
        {visibleWorkstreams.length ? (
          <Stack gap="8" display="flex" height="100%">
            {tabsHeader}
            <Box style={{flex: 1, minHeight: 0, overflowY: 'auto'}}>
              <Stack gap="2">
                {rows}
                {restrictedNote}
              </Stack>
            </Box>
            {statusUpdateModal}
          </Stack>
        ) : restrictedWorkstreamCount > 0 ? (
          <EmptyState
            hideBorder
            paddingY="24"
            marginBottom="0"
            title={t('workstreams_confidential')}
            body={t('workstreams_confidential_subtitle')}
            image={<DialLockMini altText="dial lock" width={64} height={64} />}
          />
        ) : (
          <EmptyState
            hideBorder
            paddingY="24"
            marginBottom="0"
            title={'No Workstreams just yet'}
            body={'Workstreams will show up here once added, so check again later!'}
            image={<FolderMini width={64} altText={''} />}
          />
        )}
      </Box>
    );
  }

  if (isLoading) return <ProjectSkeleton />;
  return (
    <div ref={fadeInRef}>
      {visibleWorkstreams.length ? (
        <>
          {tabsHeader}
          {rows}

          {restrictedNote}

          {visibleRows.length > limit && (
            <Box paddingTop="12">
              <Button
                variant="transparent"
                size="small"
                onClick={() =>
                  setExpandedTabs((current) => ({
                    ...current,
                    [expandedTabKey]: !isExpanded,
                  }))
                }
              >
                {isExpanded
                  ? t('show_less')
                  : t('show_more_count', {count: visibleRows.length - limit})}
              </Button>
            </Box>
          )}

          {statusUpdateModal}
        </>
      ) : restrictedWorkstreamCount > 0 ? (
        <EmptyState
          hideBorder
          paddingY="24"
          marginBottom="0"
          title={t('workstreams_confidential')}
          body={t('workstreams_confidential_subtitle')}
          image={<DialLockMini altText="dial lock" width={64} height={64} />}
        />
      ) : (
        <EmptyState
          hideBorder
          paddingY="24"
          marginBottom="0"
          title={'No Workstreams just yet'}
          body={'Workstreams will show up here once added, so check again later!'}
          image={<FolderMini width={64} altText={''} />}
        />
      )}
    </div>
  );
};

const TeamWorkstreamTab = ({
  employee,
  team,
  workstreams,
  isLoading,
  onClick,
  source = 'profile',
  maxItems,
}: {
  employee: Employee | EmployeeFull;
  team: TeamWithCountsNew;
  workstreams: WorkstreamBasic[];
  isLoading: boolean;
  onClick: (action: string) => void;
  source?: string;
  maxItems?: number;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [updateWorkstream, setUpdateWorkstream] = useState<WorkstreamBasic | undefined>(undefined);
  const limit = maxItems ?? limitProjects;
  const displayedRows = workstreams.slice(0, isExpanded ? undefined : limit);

  return (
    <SkeletonFadeIn loading={isLoading} skeleton={<ProjectSkeleton />}>
      {workstreams.length > 0 ? (
        <>
          {displayedRows.map((workstream) => (
            <WorkstreamRow
              key={workstream.id}
              employee={employee}
              onClick={onClick}
              onUpdateWorkstream={setUpdateWorkstream}
              source={source}
              workstream={workstream}
            />
          ))}

          {workstreams.length > limit && (
            <Box paddingTop="12">
              <Button
                variant="transparent"
                size="small"
                onClick={() => setIsExpanded((current) => !current)}
              >
                {isExpanded
                  ? t('show_less')
                  : t('show_more_count', {count: workstreams.length - limit})}
              </Button>
            </Box>
          )}

          <WorkstreamStatusUpdateModal
            workstreamId={updateWorkstream?.id}
            onClose={() => setUpdateWorkstream(undefined)}
            source={source}
          />
        </>
      ) : (
        <EmptyState
          hideBorder
          paddingY="24"
          marginBottom="0"
          title={'No Workstreams just yet'}
          body={`Workstreams tagged to ${team.name ?? 'this team'} will show up here once added.`}
          image={<FolderMini width={64} altText={''} />}
        />
      )}
    </SkeletonFadeIn>
  );
};

const WorkstreamRow = ({
  workstream,
  source,
  onClick,
  onUpdateWorkstream,
}: {
  employee: Employee | EmployeeFull;
  workstream: WorkstreamBasicWithOutcomes | WorkstreamBasic;
  source: string;
  onClick: (action: string) => void;
  onUpdateWorkstream: (workstream: WorkstreamBasic) => void;
  showContributorChip?: boolean;
}) => {
  const DRI = getWorkstreamDri(workstream);

  return (
    <Box
      key={workstream.id}
      padding="8"
      {...withShade({duration: 'Instant'})}
      borderRadius="Medium"
    >
      <Link
        to={`${ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', workstream.id.toString())}`}
        // From the home tile or the profile card, the workstreams page's view event says so.
        state={{source, ...getRowViaState(source)}}
        hasNoUnderline
        className={styles.workstreamLink}
        onClick={() => onClick('workstream')}
      >
        <Box display="flex" justifyContent="space-between" className={styles.workstreamRow}>
          <Box flexGrow={1} style={{minWidth: 0}}>
            <LabelGroup
              align="top"
              withText={<Text isBold>{workstream.name}</Text>}
              withSubtext={
                <Split gap="8" alignY="center">
                  {DRI && (
                    <Split.Item>
                      <Text size="small" color="subtle">
                        {DRI.name}
                      </Text>
                    </Split.Item>
                  )}

                  {DRI && workstream.privacy !== WorkstreamPrivacy.PUBLIC && (
                    <Split.Item>
                      <Box backgroundColor="Border Subtle" style={{height: 12, width: 1}} />
                    </Split.Item>
                  )}

                  {workstream.privacy !== WorkstreamPrivacy.PUBLIC && (
                    <Split.Item>
                      <Box display="flex" alignItems="center" style={{gap: 4}}>
                        <UIIcon src={LockLine} size="small" /> {t('private')}
                      </Box>
                    </Split.Item>
                  )}
                </Split>
              }
            />
          </Box>
          <Box flexShrink={0}>
            <StatusButton
              status={getWorkstreamUpdateVisualStatus(workstream.updates?.[0]?.status)}
              onClick={(e: React.SyntheticEvent) => {
                e.preventDefault();
                // Not also the row's own 'workstream' click.
                e.stopPropagation();
                onClick('status_update');
                onUpdateWorkstream(workstream);
              }}
              includeArchived
            />
          </Box>
        </Box>
      </Link>
    </Box>
  );
};

const getRowViaState = (source: string) => {
  if (source === 'dashboard') return viaState('home_tile');
  if (source === 'profile') return viaState('profile_card');
  return {};
};

const getTeamTabId = (team: TeamWithCountsNew) => `team:${team.team_id}`;

const getModeName = (mode: string) => (mode === TEAM_MODE_ID ? 'team' : 'individual');

const buildMineVisibleWorkstreams = (
  employee: Employee | EmployeeFull,
  tracker?: WorkstreamAccess
): WorkstreamWithRole[] => {
  const workstreams: WorkstreamWithRole[] = [];

  for (const dependency of tracker?.dependencies ?? []) {
    if (
      dependency.dependent.name === 'Restricted' ||
      dependency.dependent.archived ||
      isWorkstreamStatusArchived(dependency.dependent)
    ) {
      continue;
    }

    const workstream = dependency.dependent;
    const employeeRoles = getEmployeeRoles(workstream, employee);
    if (employeeRoles.length > 0) {
      for (const role of employeeRoles) {
        workstreams.push({
          role,
          workstream,
        });
      }
      continue;
    }

    const hasEmployeeAssociation = workstream.employee_associations.some((association) =>
      isAssociationForEmployee(association, employee)
    );

    if (
      hasEmployeeAssociation ||
      workstream.metrics.some((metric) => metric.owner?.user_id === employee.user_id) ||
      (workstream.jira_objects ?? []).some((jira) => jira.owner?.user_id === employee.user_id)
    ) {
      workstreams.push({
        role: CONTRIBUTOR_TAB_ID,
        workstream,
      });
    }
  }

  return workstreams.sort((left, right) => {
    const leftPriority = getStatusPriority(left.workstream.updates?.[0]?.status);
    const rightPriority = getStatusPriority(right.workstream.updates?.[0]?.status);

    if (leftPriority !== rightPriority) {
      return leftPriority - rightPriority;
    }

    const leftIsOwner = left.workstream.employee_associations?.some(
      (association) => association.employee.ldap === employee.ldap
    );
    const rightIsOwner = right.workstream.employee_associations?.some(
      (association) => association.employee.ldap === employee.ldap
    );

    if (leftIsOwner && !rightIsOwner) return -1;
    if (!leftIsOwner && rightIsOwner) return 1;

    return (left.workstream.id ?? 0) - (right.workstream.id ?? 0);
  });
};

const getVisibleTeamWorkstreams = (workstreams: WorkstreamBasic[]) =>
  [...workstreams]
    .filter((workstream) => !workstream.archived && !isWorkstreamStatusArchived(workstream))
    .sort((left, right) => {
      const leftPriority = getStatusPriority(left.updates?.[0]?.status);
      const rightPriority = getStatusPriority(right.updates?.[0]?.status);

      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }

      return left.id - right.id;
    });

const sortEmployeeTeamsForTabs = (
  employee: Employee | EmployeeFull,
  employeeTeams: TeamWithCountsNew[]
) => {
  if (employeeTeams.length === 0) {
    return [];
  }

  const allocationByTeamId = new Map(
    ('team_associations' in employee ? employee.team_associations : []).map((association) => [
      association.team.team_id,
      association.allocation,
    ])
  );

  return [...employeeTeams].sort((teamA, teamB) => {
    const allocationA = allocationByTeamId.get(teamA.team_id) ?? -1;
    const allocationB = allocationByTeamId.get(teamB.team_id) ?? -1;

    if (allocationA !== allocationB) {
      return allocationB - allocationA;
    }

    if (teamA.total_employee_count !== teamB.total_employee_count) {
      return teamB.total_employee_count - teamA.total_employee_count;
    }

    return (teamA.name ?? '').localeCompare(teamB.name ?? '');
  });
};

const ProjectSkeleton = () => (
  <Box display="flex" flexDirection="column">
    <Skeleton.Box height={44} className={atoms({marginY: '8'})} withBorderRadius={'medium'} />
    <Skeleton.Box height={44} className={atoms({marginY: '8'})} withBorderRadius={'medium'} />
    <Skeleton.Box height={44} className={atoms({marginY: '8'})} withBorderRadius={'medium'} />
    <Box marginTop="16">
      <Skeleton.Text size="small" width={50} />
    </Box>
  </Box>
);
